// deliveryEngine.js
//
// What to do with the footage after you've shot it, derived from the
// recipe rather than looked up from a platform's help page.
//
// -----------------------------------------------------------------------
// WHY THIS IS A DERIVED SECTION AND NOT A SET OF PLATFORM PAGES
//
// The obvious version of "export settings" is a page per platform:
// container, codec, bitrate, resolution, colour space. That version is a
// bad idea here, for three reasons.
//
// It's a lookup table, not a calculation. Everything else on this site is
// computed from camera capabilities, which is what makes it defensible;
// transcribing YouTube's recommended bitrate chart is the thin-content
// shape we spent this whole build removing.
//
// It goes stale in a way the rest of the site doesn't. Camera specs are
// fixed once shipped. Platform recommendations change without notice, and
// a page confidently stating last year's figures is worse than no page.
//
// And it's already answered. YouTube, TikTok and Instagram each publish
// their own specs and rank first for their own names. There is no gap.
//
// What ISN'T answered anywhere is the join between the two: given that you
// shot 4K at 100fps in D-Log M, what does that mean for your timeline,
// your grade and your crop? That depends on the capture decisions, which
// this site knows and the platform docs never will. So this module answers
// that, and points at the platform for the numbers that are theirs to
// state.
// -----------------------------------------------------------------------

// Ceilings expressed as the LONG edge, because a 9:16 platform's limit is
// 1080 wide by 1920 tall — comparing that to a capture height would say a
// 4K clip fits inside a vertical frame, which it doesn't.
const PLATFORM_CEILING = {
  "youtube-long": { longEdge: 3840, label: "4K" },
  "vertical-social": { longEdge: 1920, label: "1080×1920 vertical" },
  "website-landing": { longEdge: 1920, label: "1080p" },
  "commercial-broadcast": { longEdge: 3840, label: "4K" },
};

// Rough long-edge pixel count per resolution label, used only to decide
// whether a capture mode overshoots what a platform will keep.
function longEdgeOf(resolution) {
  const r = String(resolution || "");
  if (/8K/i.test(r)) return 7680;
  if (/6K/i.test(r)) return 6016;
  if (/5\.7K/i.test(r)) return 5760;
  if (/5\.3K/i.test(r)) return 5312;
  if (/5\.1K/i.test(r)) return 5120;
  if (/4K|DCI/i.test(r)) return 3840;
  if (/2\.7K|3K/i.test(r)) return 2704;
  if (/1080/i.test(r)) return 1920;
  return null;
}

/**
 * The frame rate the timeline should run at, which is not always the frame
 * rate you shot. This is the single most common mistake in the whole
 * pipeline: shooting 120fps for slow motion, dropping it on a 120fps
 * timeline, and wondering why it plays back at normal speed.
 */
function timelineFor(captureFps) {
  if (captureFps >= 100) {
    const base = captureFps % 25 === 0 ? 25 : 24;
    return { fps: base, conform: Math.round((captureFps / base) * 10) / 10, slowMotion: true };
  }
  if (captureFps === 60 || captureFps === 50) {
    return { fps: captureFps, conform: 1, slowMotion: false, alsoUsableAt: captureFps / 2 };
  }
  return { fps: captureFps, conform: 1, slowMotion: false };
}

// ------------------------------------------------------- editor colour setup
//
// What to set in the NLE, derived from the picture profile the recipe
// chose.
//
// This is the per-editor content worth having, and it is not a bitrate
// table. "What input colour space do I set in Resolve for D-Log M" is a
// question people genuinely search, the answer differs per camera, it's
// derivable from data already on file, and getting it wrong produces
// footage that looks broken in a way that's hard to diagnose. Export
// presets, by contrast, are published by each editor and each platform
// and change on their own schedule.
//
// Nothing here claims a menu path is current in every version — editors
// move things between releases. The colour space names are the stable
// part; where the setting lives is described in general terms.

const PROFILE_COLOR = [
  {
    match: /d-log m/i,
    space: "DJI D-Log M",
    resolve: "Set the clip's input colour space to DJI D-Gamut / D-Log in Resolve Colour Management, or apply DJI's official D-Log M to Rec.709 LUT as the first node.",
    note: "D-Log M is a different curve from full D-Log, a D-Log LUT applied to D-Log M footage will not land correctly, and the error looks like a slightly-off grade rather than an obvious fault.",
  },
  {
    match: /d-log/i,
    space: "DJI D-Log",
    resolve: "Input colour space DJI D-Gamut, gamma DJI D-Log, or DJI's D-Log to Rec.709 LUT on the first node.",
    note: null,
  },
  {
    match: /s-log3/i,
    space: "S-Gamut3.Cine / S-Log3",
    resolve: "Input colour space S-Gamut3.Cine, gamma S-Log3. Pairing S-Log3 with plain S-Gamut3 instead of the .Cine variant is the common slip and shifts saturated colour.",
    note: "S-Log3 is designed to be exposed brighter than it looks correct, if you rated it at box speed it will be noisy in the shadows once normalised.",
  },
  {
    match: /v-log/i,
    space: "V-Gamut / V-Log",
    resolve: "Input colour space V-Gamut, gamma V-Log, or Panasonic's V-Log to V-709 LUT.",
    note: null,
  },
  {
    match: /canon log 2/i,
    space: "Cinema Gamut / Canon Log 2",
    resolve: "Input colour space Cinema Gamut, gamma Canon Log 2. Log 2 holds more shadow information than Log 3 and needs more deliberate grading to get there.",
    note: null,
  },
  {
    match: /canon log/i,
    space: "Cinema Gamut / Canon Log 3",
    resolve: "Input colour space Cinema Gamut, gamma Canon Log 3.",
    note: null,
  },
  {
    match: /n-log/i,
    space: "Nikon N-Gamut / N-Log",
    resolve: "Input colour space Nikon N-Gamut, gamma N-Log, or Nikon's N-Log to Rec.709 LUT.",
    note: null,
  },
  {
    match: /gp-log/i,
    space: "GP-Log",
    resolve: "GoPro publishes a GP-Log to Rec.709 LUT, apply it as the first node rather than trying to match it manually.",
    note: null,
  },
  {
    match: /apple log/i,
    space: "Apple Log",
    resolve: "Recent Resolve and Final Cut releases recognise Apple Log directly; older versions need Apple's LUT. Check your version before assuming the tag is read automatically.",
    note: null,
  },
  {
    match: /blackmagic film/i,
    space: "Blackmagic Design Film Gen 5",
    resolve: "Resolve reads this from the BRAW metadata, leave colour science on Gen 5 rather than overriding it.",
    note: null,
  },
  {
    match: /flat log|flat/i,
    space: "Flat / Log (manufacturer LUT)",
    resolve: "There's no standard colour space tag for this profile. Use the manufacturer's own conversion LUT; a generic log-to-709 LUT will be close but not correct.",
    note: null,
  },
  {
    match: /hlg|pq/i,
    space: "Rec.2100 HLG",
    resolve: "Set an HDR timeline and output colour space to match, or convert to Rec.709 on input if the delivery is SDR.",
    note: null,
  },
];

function editorSetup(settings) {
  const profile = settings.profile || "";
  const entry = settings.requiresGrading || /hlg|pq/i.test(profile)
    ? PROFILE_COLOR.find((p) => p.match.test(profile))
    : null;

  if (!entry) {
    return {
      space: "Rec.709",
      lead: `${profile} is already a Rec.709-style image, so there is no input conversion to set. Drop the clips on a Rec.709 timeline and any editor will interpret them correctly.`,
      editors: [
        { name: "DaVinci Resolve", body: "Nothing to configure. If Colour Management is set to DaVinci YRGB Colour Managed, leave the input space on Rec.709." },
        { name: "Premiere Pro", body: "Nothing to configure. Avoid applying a log conversion LUT, on already-finished footage it will crush contrast." },
        { name: "Final Cut Pro", body: "Nothing to configure. Keep the library and project on Rec.709." },
      ],
      note: null,
    };
  }

  return {
    space: entry.space,
    lead: `This was shot in ${profile}, so the editor needs to be told what it's looking at. Until it is, the footage will look washed out on the timeline and any grade you apply is built on the wrong starting point.`,
    editors: [
      { name: "DaVinci Resolve", body: entry.resolve },
      {
        name: "Premiere Pro",
        body: `Set the clip's input LUT in Lumetri Colour → Basic Correction, or override the interpretation in Interpret Footage. Applying the conversion as a Lumetri input LUT rather than a creative one keeps it ahead of your grade in the pipeline, which is where it belongs.`,
      },
      {
        name: "Final Cut Pro",
        body: `In the Info inspector, set the clip's Camera LUT or log processing so Final Cut applies the conversion before your adjustments. Left on None, every correction you make is fighting the log curve.`,
      },
    ],
    note: entry.note,
  };
}

/**
 * @param {object} recipe   - output of generateRecipe()
 * @param {object} output   - entry from output.json (may be null)
 * @param {object} scenario - the scenario, for intent
 */
export function generateDelivery(recipe, output, scenario) {
  const s = recipe.settings;
  const steps = [];
  const timeline = timelineFor(s.fps);

  // 1. Timeline
  if (timeline.slowMotion) {
    steps.push({
      title: "Set the timeline to " + timeline.fps + "fps, not " + s.fps,
      body: `You shot ${s.fps}fps so it can be slowed down. Dropping it on a ${s.fps}fps timeline plays it back at normal speed and throws away the entire reason for shooting it that way. On a ${timeline.fps}fps timeline the same clip runs ${timeline.conform}× slower, which is the effect you were after.`,
    });
  } else if (timeline.alsoUsableAt) {
    steps.push({
      title: `Timeline at ${timeline.fps}fps, or ${timeline.alsoUsableAt}fps for a slight slow-down`,
      body: `${s.fps}fps cuts natively at ${timeline.fps}fps. It also conforms cleanly to ${timeline.alsoUsableAt}fps for a gentle 2× slow motion, which is worth knowing if a shot turns out to need it, you have that option only because you shot at ${s.fps}.`,
    });
  } else {
    steps.push({
      title: `Timeline at ${timeline.fps}fps`,
      body: `Match the timeline to the capture rate. Mixing frame rates on one timeline is doable but produces judder on anything that moves unless every clip is conformed deliberately.`,
    });
  }

  // 2. Grade
  if (s.requiresGrading) {
    steps.push({
      title: `Grade before you export`,
      body: `This was shot in ${s.profile}, which is flat by design. Exporting it ungraded publishes footage that looks washed out and desaturated, a surprisingly common mistake, because the flat image looks "fine, just a bit soft" on a phone screen. Apply a conversion LUT or grade manually first.`,
    });
  } else {
    steps.push({
      title: "No grade required",
      body: `${s.profile} is finished in camera, so this can go from card to export with nothing in between. If you want a look, apply it as a light adjustment layer rather than reaching for a LUT built for a flat profile, those expect footage this isn't.`,
    });
  }

  // 3. Framing / crop
  if (output && output.aspectRatio) {
    const vertical = /9:16/.test(output.aspectRatio);
    const captureVertical = /vertical|9:16/i.test(s.resolution || "");
    if (vertical && !captureVertical) {
      steps.push({
        title: `Crop to ${output.aspectRatio}, and expect to lose the sides`,
        body: `You shot horizontally and ${output.name} wants ${output.aspectRatio}. Cropping keeps roughly the middle third of the frame, anything you composed near an edge is going. If this platform is the main destination, shooting vertically in camera keeps resolution the crop throws away.`,
      });
    } else if (vertical && captureVertical) {
      steps.push({
        title: `Already framed for ${output.aspectRatio}`,
        body: `Shot vertically in camera, so no crop is needed and you keep the full sensor height. Keep the subject clear of the top and bottom 15%, platform interface elements sit there and vary between apps.`,
      });
    } else {
      steps.push({
        title: `Export at ${output.aspectRatio}`,
        body: output.notes,
      });
    }
  }

  // 4. Resolution ceiling
  const capturedEdge = longEdgeOf(s.resolution);
  const ceiling = output ? PLATFORM_CEILING[output.id] : null;
  if (capturedEdge && ceiling && capturedEdge > ceiling.longEdge) {
    steps.push({
      title: `Downscale to ${ceiling.label} on export`,
      body: `${s.resolution} is above what ${output ? output.name : "this platform"} keeps, so it gets re-encoded either way. Downscaling yourself in the edit gives a cleaner result than letting the platform's encoder do it in one pass, and the extra capture resolution still earned its place, because it's what let you crop and stabilise without softening.`,
    });
  } else if (capturedEdge && ceiling && capturedEdge < ceiling.longEdge) {
    steps.push({
      title: "Export at the capture resolution, don't upscale",
      body: `${output ? output.name : "This platform"} accepts up to ${ceiling.label}, but upscaling to reach it adds no detail and spends bitrate that would otherwise go to the image you actually have. Export at ${s.resolution}.`,
    });
  }

  // 5. HDR path, only when it's real
  if (/hlg|pq|dolby/i.test(s.profile || "")) {
    steps.push({
      title: "This is an HDR capture, commit to it or convert it",
      body: `${s.profile} only pays off if the timeline, the export and the viewer's screen all carry HDR. Half a pipeline produces footage that looks flat and desaturated to most people. If you aren't set up for HDR end to end, convert to Rec. 709 in the grade rather than exporting it as-is.`,
    });
  }

  return {
    timeline,
    steps,
    editorSetup: editorSetup(s),
    // Deliberately not stating codec or bitrate numbers: those are the
    // platform's to publish and they change. Pointing at the source is
    // more useful than a figure that might be a year out of date.
    numbersNote:
      "Container, codec and bitrate are worth taking from the platform's own current specification rather than any third-party page, this one included, those numbers change without notice and a stale figure is worse than no figure.",
  };
}
